import { useEffect, useMemo, useState } from "react";
import "./ProfilingReport.css";
import { createProfilingTimeline } from "./profilingTimeline";
import { formatStopwatchElapsed } from "./usePaymentStopwatch";

const normalizeElapsed = (value) =>
  Number.isFinite(Number(value)) ? Math.max(Number(value), 0) : 0;

const formatCoverage = (measuredMilliseconds, totalMilliseconds) => {
  const total = normalizeElapsed(totalMilliseconds);

  if (total <= 0) {
    return "0%";
  }

  return `${Math.round(Math.min(measuredMilliseconds / total, 1) * 100)}%`;
};

const getSharePercent = (elapsedMilliseconds, totalMilliseconds) => {
  const total = normalizeElapsed(totalMilliseconds);

  if (total <= 0) {
    return 0;
  }

  return Math.min((normalizeElapsed(elapsedMilliseconds) / total) * 100, 100);
};

const formatCompactDuration = (value) => {
  const elapsed = normalizeElapsed(value);

  if (elapsed < 1000) {
    return `${Math.round(elapsed)}ms`;
  }

  const totalSeconds = elapsed / 1000;
  if (totalSeconds < 60) {
    const roundedSeconds = Math.round(totalSeconds * 10) / 10;
    return `${
      Number.isInteger(roundedSeconds)
        ? roundedSeconds
        : roundedSeconds.toFixed(1)
    }s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds - minutes * 60);

  if (seconds >= 60) {
    return `${minutes + 1}m`;
  }

  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
};

const getEventUrl = (event = {}) =>
  event.url || event.requestUrl || event.href || event.endpoint || "";

const getEventStatus = (event = {}) => {
  const status = Number(event.status);

  if (event.error || event.ok === false) {
    return "error";
  }

  if (Number.isFinite(status) && status >= 400) {
    return "error";
  }

  if (
    event.ok === true ||
    (Number.isFinite(status) && status >= 200 && status < 400)
  ) {
    return "success";
  }

  return "unknown";
};

const getEventStatusLabel = (event = {}) => {
  const status = Number(event.status);
  const eventStatus = getEventStatus(event);

  if (Number.isFinite(status)) {
    return `${status} ${eventStatus}`;
  }

  if (eventStatus === "error") {
    return "Error";
  }

  if (eventStatus === "success") {
    return "Success";
  }

  return "Unknown";
};

const getTableStatusLabel = (event = {}) => {
  const status = Number(event.status);

  if (event.action === "fetch" && Number.isFinite(status)) {
    return String(status);
  }

  return getEventStatusLabel(event);
};

const formatTableTime = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3
  }).format(date);
};

const getActionLabel = (event = {}, index = 0) =>
  event.label ||
  event.name ||
  event.description ||
  getEventUrl(event) ||
  event.action ||
  `Action ${index + 1}`;

const createActionRow = (event, index) => {
  const elapsed = normalizeElapsed(event?.elapsed);

  return {
    id: `event-${index}`,
    event,
    index,
    label: getActionLabel(event, index),
    type: event?.action || "action",
    status: getEventStatus(event),
    statusLabel: getEventStatusLabel(event),
    tableStatusLabel: getTableStatusLabel(event),
    started: event?.start,
    ended: event?.end,
    startLabel: formatTableTime(event?.start),
    endLabel: formatTableTime(event?.end),
    elapsed
  };
};

const TimeShareCell = ({ elapsed, total, status }) => {
  const sharePercent = getSharePercent(elapsed, total);
  const elapsedLabel = formatCompactDuration(elapsed);

  return (
    <td className="profiling-time-cell" title={`${elapsedLabel} of total time`}>
      <span className="profiling-time-bar" aria-hidden="true">
        <span
          className={`profiling-time-fill profiling-time-fill-${status}`}
          style={{ width: `${sharePercent}%` }}
        ></span>
      </span>
    </td>
  );
};

function ProfilingActionDataModal({ row, onClose }) {
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      onClose();
    };

    window.addEventListener("keydown", closeOnEscape, true);

    return () => {
      window.removeEventListener("keydown", closeOnEscape, true);
    };
  }, [onClose]);

  return (
    <div className="profiling-action-modal-backdrop" onClick={onClose}>
      <section
        className="deposit-history-panel deposit-history-data-panel profiling-action-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="profiling-action-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="deposit-history-header">
          <div>
            <h2 id="profiling-action-title">Action data</h2>
            <p>{row.label}</p>
          </div>
          <button
            type="button"
            className="history-close-button"
            onClick={onClose}
            aria-label="Close action data"
          >
            <span className="close-icon" aria-hidden="true"></span>
          </button>
        </div>

        <pre className="deposit-history-json profiling-action-json">
          {JSON.stringify(row.event, null, 2)}
        </pre>
      </section>
    </div>
  );
}

function ProfilingReport({ elapsedMilliseconds, profiling, onClose }) {
  const [selectedRow, setSelectedRow] = useState(null);
  const [isActionsScrolled, setIsActionsScrolled] = useState(false);
  const [sortMode, setSortMode] = useState("order");
  const [includedRowIds, setIncludedRowIds] = useState(() => new Set());

  const events = useMemo(
    () => (Array.isArray(profiling?.events) ? profiling.events : []),
    [profiling?.events]
  );
  const totalElapsedMilliseconds = normalizeElapsed(
    profiling?.totalElapsedMilliseconds ?? elapsedMilliseconds ?? 0
  );
  const rows = useMemo(() => {
    const actionRows = events.map(createActionRow);

    if (sortMode === "elapsed") {
      return [...actionRows].sort(
        (left, right) =>
          right.elapsed - left.elapsed || left.index - right.index
      );
    }

    return actionRows;
  }, [events, sortMode]);
  const includedEvents = useMemo(
    () => events.filter((_, index) => includedRowIds.has(`event-${index}`)),
    [events, includedRowIds]
  );
  const includedTimeline = useMemo(
    () =>
      createProfilingTimeline({
        totalElapsedMilliseconds,
        startedAt: profiling?.startedAt,
        events: includedEvents
      }),
    [includedEvents, profiling?.startedAt, totalElapsedMilliseconds]
  );
  const includedMeasuredMilliseconds = includedTimeline.measuredMilliseconds;
  const includedUnmeasuredMilliseconds = Math.max(
    totalElapsedMilliseconds - includedMeasuredMilliseconds,
    0
  );
  const hasTableRows = rows.length > 0 || totalElapsedMilliseconds > 0;

  const toggleIncludedRow = (rowId) => {
    setIncludedRowIds((current) => {
      const next = new Set(current);

      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }

      return next;
    });
  };

  useEffect(() => {
    setSelectedRow(null);
    setIsActionsScrolled(false);
    setSortMode("order");
    setIncludedRowIds(new Set(events.map((_, index) => `event-${index}`)));
  }, [events, profiling]);

  return (
    <section
      className="deposit-history-panel deposit-history-data-panel profiling-report"
      role="dialog"
      aria-modal="true"
      aria-labelledby="profiling-report-title"
      aria-label="Profiling data"
    >
      <div className="deposit-history-header profiling-report-header">
        <div>
          <h2 id="profiling-report-title">Profiling data</h2>
          <p>{rows.length} measured actions</p>
        </div>
        <button
          type="button"
          className="history-close-button"
          onClick={onClose}
          aria-label="Close profiling data"
        >
          <span className="close-icon" aria-hidden="true"></span>
        </button>
      </div>

      <div className="profiling-report-body">
        <div className="profiling-summary" aria-label="Profiling summary">
          <span>
            Total{" "}
            <strong>{formatStopwatchElapsed(totalElapsedMilliseconds)}</strong>
          </span>
          <span>
            Measured{" "}
            <strong>
              {formatStopwatchElapsed(includedMeasuredMilliseconds)}
            </strong>
          </span>
          <span className="profiling-summary-warning">
            Unmeasured{" "}
            <strong>
              {formatStopwatchElapsed(includedUnmeasuredMilliseconds)}
            </strong>
          </span>
          <span>
            Coverage{" "}
            <strong>
              {formatCoverage(
                includedMeasuredMilliseconds,
                totalElapsedMilliseconds
              )}
            </strong>
          </span>
          <span>
            Actions <strong>{rows.length}</strong>
          </span>
          <span
            className="profiling-sort-control"
            role="group"
            aria-label="Sort profiling actions"
          >
            Sort
            <button
              type="button"
              className={`profiling-sort-button ${
                sortMode === "order" ? "active" : ""
              }`}
              aria-pressed={sortMode === "order"}
              onClick={() => setSortMode("order")}
            >
              Order
            </button>
            <button
              type="button"
              className={`profiling-sort-button ${
                sortMode === "elapsed" ? "active" : ""
              }`}
              aria-pressed={sortMode === "elapsed"}
              onClick={() => setSortMode("elapsed")}
            >
              Elapsed
            </button>
          </span>
        </div>

        {!hasTableRows ? (
          <div className="profiling-empty">
            No profiling actions were captured for this attempt.
          </div>
        ) : (
          <div
            className={
              isActionsScrolled
                ? "profiling-actions-panel profiling-actions-panel-scrolled"
                : "profiling-actions-panel"
            }
            onScroll={(event) =>
              setIsActionsScrolled(event.currentTarget.scrollTop > 0)
            }
          >
            <table className="profiling-actions-table">
              <thead>
                <tr>
                  <th className="profiling-include-cell"></th>
                  <th>Action</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th className="profiling-started-cell">Started</th>
                  <th className="profiling-elapsed-cell">Elapsed</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {totalElapsedMilliseconds > 0 && (
                  <tr
                    className="profiling-unmeasured-row"
                    aria-label="Unmeasured time"
                  >
                    <td className="profiling-include-cell"></td>
                    <td title="Unmeasured">Unmeasured</td>
                    <td>-</td>
                    <td
                      className="profiling-status profiling-status-unmeasured"
                      title="Unmeasured"
                    >
                      unmeasured
                    </td>
                    <td className="profiling-started-cell">-</td>
                    <td
                      className="profiling-elapsed-cell"
                      title={formatStopwatchElapsed(
                        includedUnmeasuredMilliseconds
                      )}
                    >
                      {formatCompactDuration(includedUnmeasuredMilliseconds)}
                    </td>
                    <TimeShareCell
                      elapsed={includedUnmeasuredMilliseconds}
                      total={totalElapsedMilliseconds}
                      status="unmeasured"
                    />
                  </tr>
                )}
                {rows.map((row) => {
                  const isIncluded = includedRowIds.has(row.id);

                  return (
                    <tr
                      key={row.id}
                      className={isIncluded ? "" : "profiling-row-excluded"}
                      tabIndex={0}
                      onClick={() => setSelectedRow(row)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedRow(row);
                        }
                      }}
                    >
                      <td
                        className="profiling-include-cell"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isIncluded}
                          aria-label={`Include ${row.label} in totals`}
                          onChange={() => toggleIncludedRow(row.id)}
                          onKeyDown={(event) => event.stopPropagation()}
                        />
                      </td>
                      <td title={row.label}>{row.label}</td>
                      <td title={row.type}>{row.type}</td>
                      <td
                        className={`profiling-status profiling-status-${row.status}`}
                        title={row.statusLabel}
                      >
                        {row.tableStatusLabel}
                      </td>
                      <td
                        className="profiling-started-cell"
                        title={row.started || row.startLabel}
                      >
                        {row.startLabel}
                      </td>
                      <td
                        className="profiling-elapsed-cell"
                        title={formatStopwatchElapsed(row.elapsed)}
                      >
                        {formatCompactDuration(row.elapsed)}
                      </td>
                      <TimeShareCell
                        elapsed={isIncluded ? row.elapsed : 0}
                        total={totalElapsedMilliseconds}
                        status={isIncluded ? row.status : "excluded"}
                      />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedRow && (
        <ProfilingActionDataModal
          row={selectedRow}
          onClose={() => setSelectedRow(null)}
        />
      )}
    </section>
  );
}

export default ProfilingReport;
